import { Date as LiturgicalDate } from './calendar.ts';

export type DateString = string; // "YYYY-MM-DD"

export const millisecondsPerDay = 1000 * 60 * 60 * 24;

export const oldestDate = '1000-01-01';
export const newestDate = '3000-01-01'; // someday this will need to be extended :)

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export function getMonthName(date: DateString): string {
  return getMonthNameFromRawDate(dateFromString(date));
}

export function getMonthNameFromRawDate(date: Date): string {
  const monthIndex = date.getMonth();
  switch (monthIndex) {
    case 0:
      return 'January';
    case 1:
      return 'February';
    case 2:
      return 'March';
    case 3:
      return 'April';
    case 4:
      return 'May';
    case 5:
      return 'June';
    case 6:
      return 'July';
    case 7:
      return 'August';
    case 8:
      return 'September';
    case 9:
      return 'October';
    case 10:
      return 'November';
    case 11:
      return 'December';
    default:
      throw new Error(`ERROR: Unexpected month index: ${monthIndex}`);
  }
}

export function getDayOfMonth(date: DateString): number {
  return dateFromString(date).getDate();
}

export function getDayOfYear(date: DateString): number {
  return Math.round((dateFromString(date).getTime() - new Date(getYear(date), 0, 0).getTime()) / millisecondsPerDay);
}

export function getYear(date: DateString): number {
  return dateFromString(date).getFullYear();
}

export function getDateForMonth(monthName: string, year: number): string {
  return createDateString(year, getMonthIndex(monthName) + 1, 1);
}

export function getMonthIndex(monthName: string): number {
  switch (monthName) {
    case 'January':
      return 0;
    case 'February':
      return 1;
    case 'March':
      return 2;
    case 'April':
      return 3;
    case 'May':
      return 4;
    case 'June':
      return 5;
    case 'July':
      return 6;
    case 'August':
      return 7;
    case 'September':
      return 8;
    case 'October':
      return 9;
    case 'November':
      return 10;
    case 'December':
      return 11;
    default:
      throw new Error(`ERROR: Unexpected month name: ${monthName}`);
  }
}

export function createDateString(year: number, month: number, day: number): DateString {
  const yearString = year.toString();
  const monthString = month.toString().padStart(2, '0');
  const dayString = day.toString().padStart(2, '0');
  return `${yearString}-${monthString}-${dayString}`;
}

export function dateStringFromDate(date: Date): DateString {
  // note that month is zero based
  return createDateString(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function splitDateString(date: DateString): { year: number; month: number; day: number } | undefined {
  const match = date.match(/^(?<year>\d\d\d\d)-(?<month>\d\d)-(?<day>\d\d)/);
  if (!match || !match.groups) {
    return;
  }
  const { year, month, day } = match.groups;
  if (!year || !month || !day) {
    return;
  }
  // note that month is zero based
  const result = { year: Number(year), month: Number(month) - 1, day: Number(day) };
  if (isNaN(result.year) || isNaN(result.month) || isNaN(result.day)) {
    return;
  }
  return result;
}

export function isValidDateString(date: DateString | undefined): date is DateString {
  if (!date) {
    return false;
  }
  const { year, month, day } = { ...splitDateString(date) };
  if (year === undefined || month === undefined || day === undefined) {
    return false;
  }
  return month <= 11 && day <= 31 && year >= 0 && month >= 0 && day >= 1 && year.toString().length === 4;
}

export function validateDateString(date: DateString | undefined) {
  if (!isValidDateString(date)) {
    throw new Error(`Invalid date string "${date}"`);
  }
}

export function dateFromString<T extends DateString | undefined>(date: T): T extends DateString ? Date : undefined;
export function dateFromString(date: DateString | undefined): Date | undefined {
  if (!date) {
    return;
  }
  const match = splitDateString(date);
  if (!match) {
    return;
  }
  return new Date(match.year, match.month, match.day);
}

export const DaysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function getDayOfWeekFromDateString(dateString: DateString): string {
  const date = dateFromString(dateString);
  return DaysOfWeek[date.getDay()];
}

// Gives the date of the specified day of the week relative to the given date.
// Thus, if forDate is 11/27/2024 (a Wednesday), asking for a Tuesday (dayIndex = 2) yields 11/26/2024.
export function getDateOfDay(
  dayIndex: number,
  forDate: DateString,
  { treatSundayAsLast }: { treatSundayAsLast?: boolean } = {},
): DateString {
  const date = dateFromString(forDate);
  date.setDate(date.getDate() - date.getDay() + dayIndex - (treatSundayAsLast && date.getDay() === 0 ? 7 : 0));
  return dateStringFromDate(date);
}

export function thisSundayDate(forDate: DateString): DateString {
  const day = getDayOfWeek(forDate);
  return getDateFromOffset(day === 0 ? 0 : 7 - day, forDate);
}

export function nextSundayDate(forDate: DateString): DateString {
  const day = getDayOfWeek(forDate);
  return getDateFromOffset(7 - day + (day === 0 ? 0 : 7), forDate);
}

export function lastSundayDate(forDate: DateString): DateString {
  const day = getDayOfWeek(forDate);
  return getDateFromOffset(-day - (day === 0 ? 7 : 0), forDate);
}

export function mostRecentSunday(forDate: DateString): DateString {
  return getDayOfWeek(forDate) === DayOfWeek.Sunday ? forDate : lastSundayDate(forDate);
}

function getDateFromOffset(days: number, forDate: DateString): DateString {
  const date = dateFromString(forDate);
  date.setDate(date.getDate() + days);
  return dateStringFromDate(date);
}

export function addDayOffset(dateString: DateString, days: number): DateString {
  const date = dateFromString(dateString);
  date.setDate(date.getDate() + days);
  return dateStringFromDate(date);
}

export function getDayOfWeek(date: DateString): DayOfWeek {
  return dateFromString(date).getDay();
}

// the Morning Huddle runs at 8 AM Mountain, so "today" must be computed in this zone rather than UTC
export const mountainTimeZone = 'America/Denver';

// Gives the calendar date in the given time zone for the given instant.
// Intl handles daylight saving transitions, which hand-rolled UTC offset arithmetic does not.
export function dateStringInTimeZone(instant: Date, timeZone: string): DateString {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  return createDateString(value('year'), value('month'), value('day'));
}

// Gives the day of the week in the given time zone for the given instant.
// Saturday 11 PM Mountain is already Sunday in UTC, so the zone matters.
export function dayOfWeekInTimeZone(instant: Date, timeZone: string): DayOfWeek {
  return getDayOfWeek(dateStringInTimeZone(instant, timeZone));
}

// Gives the instant at which the given wall-clock time occurs on the given date in the given zone -
// the inverse of dateStringInTimeZone.  "2 AM Mountain" is a different instant in summer than in
// winter, so the zone's offset has to be read at the answer rather than assumed.
//
// The offset is read twice because the first read uses a guess that can land on the wrong side of a
// daylight saving transition; the second read is taken at the corrected instant, which is the one
// whose offset actually applies.  ICU knows the transition dates, so nothing here is hand-rolled.
export function instantAtTimeInTimeZone(date: DateString, hour: number, minute: number, timeZone: string): Date {
  const { year, month, day } = splitDateString(date) ?? {};
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Invalid date string: ${date}`);
  }
  // month from splitDateString is zero based, as Date.UTC wants
  const wallClockAsUtc = Date.UTC(year, month, day, hour, minute);
  const firstGuess = wallClockAsUtc - timeZoneOffsetMs(new Date(wallClockAsUtc), timeZone);
  return new Date(wallClockAsUtc - timeZoneOffsetMs(new Date(firstGuess), timeZone));
}

// How far ahead of UTC the zone is at the given instant, in milliseconds (negative in the Americas).
function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    // h23 rather than hour12: false - some ICU versions render midnight as hour 24 otherwise
    hourCycle: 'h23',
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value);
  const wallClockAsUtc = Date.UTC(
    value('year'),
    value('month') - 1,
    value('day'),
    value('hour'),
    value('minute'),
    value('second'),
  );
  // the formatted wall clock has no sub-second part, so compare against a whole second
  return wallClockAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

export function thisMonthDate(forDate: DateString): DateString {
  const date = dateFromString(forDate);
  return createDateString(date.getFullYear(), date.getMonth() + 1, 1);
}

export function todayDate(): DateString {
  return dateStringFromDate(new Date());
}

export function getTomorrowsDateForPST(): DateString {
  // PST = UTC-8 (timezoneOffset = 8)
  const timezoneOffsetPST = 8;
  const timezoneOffset = new Date().getTimezoneOffset() / 60;
  const tomorrowPST = new Date(new Date().getTime() + (timezoneOffset - timezoneOffsetPST) * 60 * 60 * 1000);
  tomorrowPST.setDate(tomorrowPST.getDate() + 1);
  return dateStringFromDate(tomorrowPST);
}

// even number of days between two dates, zero if same day
export function daysBetween(start: DateString, end: DateString): number {
  return Math.round((Number(dateFromString(end)) - Number(dateFromString(start))) / millisecondsPerDay);
}

// gives the number of weeks between two dates, rounding down
export function weeksBetween(start: DateString, end: DateString): number {
  return Math.floor(daysBetween(start, end) / 7);
}

export function sortDates(dates: DateString[]): DateString[] {
  return [...dates].sort((lhs, rhs) => dateCompare(lhs, rhs));
}

export function dateCompare(lhs: DateString, rhs: DateString): number {
  return lhs.localeCompare(rhs);
}

export function minDate(dates: DateString[]): DateString {
  if (dates.length === 0) {
    throw new Error('Cannot take minimum of an empty list');
  }
  return sortDates(dates)[0];
}

export function maxDate(dates: DateString[]): DateString {
  if (dates.length === 0) {
    throw new Error('Cannot take minimum of an empty list');
  }
  return sortDates(dates)[dates.length - 1];
}

// from https://stackoverflow.com/a/6117889/998490
export function getWeekNumber(date: DateString): number {
  const dateCopy = dateFromString(date);
  const dayNum = dateCopy.getUTCDay() || 7;
  dateCopy.setUTCDate(dateCopy.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dateCopy.getUTCFullYear(), 0, 1));
  return Math.ceil(((Number(dateCopy) - Number(yearStart)) / 86400000 + 1) / 7);
}

export function firstDayOfWeek(forDate: DateString): DateString {
  return addDayOffset(forDate, -dateFromString(forDate).getDay());
}

export function lastDayOfWeek(forDate: DateString): DateString {
  return addDayOffset(forDate, 6 - dateFromString(forDate).getDay());
}

export function getFirstDayOfMonth(forDate: DateString): DateString {
  const date = dateFromString(forDate);
  return dateStringFromDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

export function getLastDayOfMonth(forDate: DateString): DateString {
  const date = dateFromString(forDate);
  return dateStringFromDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
}

export function minDateAbsolute(): DateString {
  return createDateString(1500, 1, 1);
}

export function maxDateAbsolute(): DateString {
  return createDateString(3000, 1, 1);
}

export function getAllEnclosingSundays(min: DateString, max: DateString): DateString[] {
  const sundays: DateString[] = [];
  for (let sunday = lastSundayDate(min); sunday < nextSundayDate(max); sunday = addDayOffset(sunday, 7)) {
    sundays.push(sunday);
  }
  return sundays;
}

// converts format '1899-12-30T18:30:00.000-08:00' to '1899-12-30'
export function getDateFromDateTime(dateTime: string): DateString {
  const date = dateTime.match(/^(?<date>\d\d\d\d-\d\d-\d\d)T.*/)?.groups?.date;
  if (!date) {
    throw new Error(`Invalid date time format: ${dateTime}`);
  }
  return date;
}

// returns MM/DD/YYYY
export function getVisibleDate(date: DateString): string {
  return (
    `${(getMonthIndex(getMonthName(date)) + 1).toString().padStart(2, '0')}` +
    `/${getDayOfMonth(date).toString().padStart(2, '0')}/${getYear(date)}`
  );
}

export function getHymnOfTheMonthDate(forDate: DateString): DateString {
  return thisMonthDate(thisSundayDate(forDate));
}

export interface DateRange {
  start: DateString;
  end: DateString;
}

export function dateRangesOverlap(lhs: DateRange, rhs: DateRange): boolean {
  return lhs.start <= rhs.end && rhs.start <= lhs.end;
}

export function dateRangeOverlapsDate(range: DateRange, date: DateString): boolean {
  return date >= range.start && date <= range.end;
}

export function getDateAsShortString(dateStr: DateString): string {
  const date = dateFromString(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

export function convertLiturgicalDateToDateString(date: LiturgicalDate): DateString {
  return createDateString(date.year, date.month, date.day);
}
